import { useState, useEffect, useMemo } from 'react';
import { FunnelStage } from './components/FunnelStage';
import { TerminalStages } from './components/TerminalStages';
import { SummaryCards } from './components/SummaryCards';
import { MobileFunnelCard } from './components/MobileFunnelCard';
import { Button } from '../../components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import { useFunnelData } from './hooks/useFunnelData';
import { STAGE_CONFIG } from './types';
import { getDateFilterOptions, getDefaultFilterOption, formatDateForSQL, type DateFilterOption } from './utils/dateFilterUtils';
import { openDrawerModal } from '../../components/DrawerModal';
import OppsAndRisk from '../OppsAndRisks';

interface SalesFunnelProps {
  onStageClick?: (stage: string) => void;
  onAtRiskClick?: () => void;
}

export default function Funnel({ onStageClick, onAtRiskClick }: SalesFunnelProps = {}) {
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);

  // Generate dynamic date filter options
  const dateFilterOptions = useMemo(() => getDateFilterOptions(), []);
  const defaultOption = useMemo(() => getDefaultFilterOption(), []);

  const [selectedFilter, setSelectedFilter] = useState<DateFilterOption>(defaultOption);

  // Data fetching with date filter
  const { data, loading, error } = useFunnelData({
    startDate: formatDateForSQL(selectedFilter.startDate),
    endDate: formatDateForSQL(selectedFilter.endDate),
  });


  // Listen to window resize
  useEffect(() => {
    const handleResize = () => setIsMobile(window.innerWidth < 768);
    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Map API data to component format (NEW: with all new props)
  const stages = data
    ? data.stages.map((stage, index) => {
        // Map conversion rate based on stage position
        let conversionRate = 0;
        if (index === 0) conversionRate = data.conversion_rates.prospect_to_prepare_qualify;
        else if (index === 1) conversionRate = data.conversion_rates.prepare_qualify_to_present;
        else if (index === 2) conversionRate = data.conversion_rates.present_to_follow_up;

        return {
          name: STAGE_CONFIG[stage.stage].label,
          opportunities: stage.count,
          avgValue: `$${(stage.avg_value / 1000).toFixed(1)}K`,          // FIXED ROUNDING
          totalValue: `$${Math.round(stage.total_value / 1000)}K`,       // NEW PROP
          probability: stage.stage_probability,
          atRisk: stage.at_risk_count,
          riskLevel: (stage.at_risk_count > 5 ? 'high' : stage.at_risk_count > 2 ? 'medium' : 'low') as 'low' | 'medium' | 'high',
          conversionRate,
          weightedValue: `$${Math.round(stage.weighted_value / 1000)}K`, // NEW PROP
          avgDays: stage.avg_days_in_stage,                              // NEW PROP
          agingDeals: stage.aging_deals_count,                           // FIXED: from backend (deals >60 days old)
        };
      })
    : [];

  const closedWon = data?.closed.find((c) => c.stage === 'closed_won');
  const closedLost = data?.closed.find((c) => c.stage === 'closed_lost');

  const terminalStages = data
    ? {
        won: {
          opportunities: closedWon?.count || 0,
          value: `$${Math.round((closedWon?.total_value || 0) / 1000)}K`,
        },
        lost: {
          opportunities: closedLost?.count || 0,
          value: `$${Math.round((closedLost?.total_value || 0) / 1000)}K`,
        },
      }
    : {
        won: { opportunities: 0, value: '$0' },
        lost: { opportunities: 0, value: '$0' },
      };

  // Calculate win rate (NEW) - Fixed: use closed deals only, not active opportunities
  const totalClosedDeals = (closedWon?.count || 0) + (closedLost?.count || 0);
  const winRate = totalClosedDeals > 0
    ? ((closedWon?.count || 0) / totalClosedDeals * 100).toFixed(1)
    : '0.0';

  // Calculate sales velocity (NEW) - FIXED: Industry-standard pipeline velocity
  // Formula: (Weighted Pipeline / Avg Sales Cycle Days) × 30 days/month ÷ 1000 for K notation
  // This shows "how much revenue flows through pipeline per month"
  const salesVelocity = data && data.summary.avg_sales_cycle_days
    ? `$${((data.summary.weighted_pipeline / data.summary.avg_sales_cycle_days) * 30 / 1000).toFixed(1)}K/mo`
    : '$0/mo';

  // Calculate avg cycle (NEW) - FIXED: Backend now calculates from closed deals
  const avgCycle = data ? `${data.summary.avg_sales_cycle_days}d` : '42d';

  const summary = data
    ? {
        weighted: `$${Math.round(data.summary.weighted_pipeline / 1000)}K`,
        winRate: `${winRate}%`,        // NEW
        atRisk: data.summary.total_at_risk,
        salesVelocity,                 // NEW
        avgCycle,                      // NEW
      }
    : {
        weighted: '$0',
        winRate: '0%',
        atRisk: 0,
        salesVelocity: '$0/mo',
        avgCycle: '0d',
      };


  const handleStageClick = (stageName: string) => {
    openDrawerModal(
      <OppsAndRisk
        initialTab="opps"
        initialFilter={stageName}
        startDate={formatDateForSQL(selectedFilter.startDate)}
        endDate={formatDateForSQL(selectedFilter.endDate)}
      />
    );
  };

  const handleAtRiskClick = (stageName: string) => {
    openDrawerModal(
      <OppsAndRisk
        initialTab="risks"
        initialFilter={stageName}
        startDate={formatDateForSQL(selectedFilter.startDate)}
        endDate={formatDateForSQL(selectedFilter.endDate)}
      />
    );
  };

  const handleViewAtRisk = () => {
    openDrawerModal(
      <OppsAndRisk
        initialTab="risks"
        initialFilter="All At Risk"
        startDate={formatDateForSQL(selectedFilter.startDate)}
        endDate={formatDateForSQL(selectedFilter.endDate)}
      />
    );
  };

  const handleViewAll = () => {
    openDrawerModal(
      <OppsAndRisk
        initialTab="opps"
        initialFilter="All"
        startDate={formatDateForSQL(selectedFilter.startDate)}
        endDate={formatDateForSQL(selectedFilter.endDate)}
      />
    );
  };

  if (loading) {
    return <div>Loading funnel data...</div>;
  };

  if (error) {
    return <div>Error: {error}</div>;
  }

  return (
    <div className={`funnel-container bg-white ${isMobile ? 'py-4 overflow-y-auto hide-scrollbar' : 'p-8'}`}>
        {isMobile ? (
          // Mobile Layout
          <div className="space-y-1.5">
            {/* Sticky Summary Header */}
            <div className="bg-white px-3 py-2 border-b border-gray-200 sticky top-0 z-10">
              {/* Option A - Compact 2 rows */}
              <div className="space-y-2.5">
                {/* Row 1: Main 3 metrics */}
                <div className="flex justify-between items-start gap-2">
                  <div className="flex-1 min-w-0 text-center">
                    <p className="text-xs text-gray-400">Weighted</p>
                    <p className="text-gray-900 text-lg">{summary.weighted}</p>
                  </div>
                  <div className="flex-1 min-w-0 text-center">
                    <p className="text-xs text-gray-400">Win Rate</p>
                    <p className="text-gray-900 text-lg">{summary.winRate}</p>
                  </div>
                  <div className="flex-1 min-w-0 text-center">
                    <p className="text-xs text-gray-400">At-Risk</p>
                    <p className="text-rose-600 text-lg">{summary.atRisk}</p>
                  </div>
                </div>

                {/* Row 2: Secondary metrics */}
                <div className="flex justify-center items-start gap-8">
                  <div className="text-center">
                    <p className="text-xs text-gray-400">Velocity</p>
                    <p className="text-gray-900 text-sm">{summary.salesVelocity}</p>
                  </div>
                  <div className="text-center">
                    <p className="text-xs text-gray-400">Avg Cycle</p>
                    <p className="text-gray-900 text-sm">{summary.avgCycle}</p>
                  </div>
                </div>
              </div>

              {/* Filter Dropdown - Dynamic Options */}
              <div className="mt-2">
                <p className="text-xs text-gray-500 mb-1">By expected close</p>
                <Select
                  value={selectedFilter.value}
                  onValueChange={(value) => {
                    const option = dateFilterOptions.find(opt => opt.value === value);
                    if (option) setSelectedFilter(option);
                  }}
                >
                  <SelectTrigger
                    className="w-full h-7 border-0 border-b border-gray-200 rounded-none px-0 text-xs hover:border-gray-400 focus-visible:border-[#4A90A4] focus-visible:ring-0 bg-white"
                  >
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="bg-white">
                    {dateFilterOptions.map((option) => (
                      <SelectItem key={option.value} value={option.value}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            </div>

            {/* Vertical Stacked Cards */}
            <div className="px-3 space-y-1.5">
              {stages.map((stage, index) => (
                <MobileFunnelCard
                  key={stage.name}
                  {...stage}
                  stageIndex={index}
                  onCardClick={() => handleStageClick(stage.name)}
                  onAtRiskClick={() => handleAtRiskClick(stage.name)}
                />
              ))}
            </div>

            {/* Terminal Stages - Side by Side */}
            <div className="px-3 grid grid-cols-2 gap-1.5">
              <div
                onClick={() => handleStageClick('Closed Won')}
                className="bg-white rounded-lg p-2 border border-emerald-200 hover:border-emerald-300 transition-colors cursor-pointer">
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="w-5 h-5 rounded bg-emerald-50 flex items-center justify-center flex-shrink-0">
                    <span className="text-emerald-600 text-xs">✓</span>
                  </div>
                  <span className="text-gray-900 text-xs">Won</span>
                </div>
                <p className="text-gray-900 text-sm">{terminalStages.won.opportunities}</p>
                <p className="text-xs text-gray-400">{terminalStages.won.value}</p>
              </div>

              <div
                onClick={() => handleStageClick('Closed Lost')}
                className="bg-white rounded-lg p-2 border border-gray-200 hover:border-gray-300 transition-colors cursor-pointer">
                <div className="flex items-center gap-1.5 mb-1">
                  <div className="w-5 h-5 rounded bg-gray-100 flex items-center justify-center flex-shrink-0">
                    <span className="text-gray-500 text-xs">✗</span>
                  </div>
                  <span className="text-gray-900 text-xs">Lost</span>
                </div>
                <p className="text-gray-900 text-sm">{terminalStages.lost.opportunities}</p>
                <p className="text-xs text-gray-400">{terminalStages.lost.value}</p>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="px-3 grid grid-cols-2 gap-1.5">
              <Button
                onClick={handleViewAtRisk}
                className="w-full bg-rose-600 hover:bg-rose-700 text-white h-8 text-xs"
              >
                At-Risk ({summary.atRisk})
              </Button>
              <Button
                onClick={handleViewAll}
                variant="outline"
                className="w-full h-8 border-gray-300 text-xs"
              >
                View All
              </Button>
            </div>
          </div>
        ) : (
          // Desktop Layout
          <div className="space-y-8">
            {/* Summary Cards */}
            <div>
              <SummaryCards
                weighted={summary.weighted}
                winRate={summary.winRate}
                atRisk={summary.atRisk}
                salesVelocity={summary.salesVelocity}
                avgCycle={summary.avgCycle}
              />
            </div>

            {/* Active Pipeline Funnel */}
            <div>
              <div className="flex items-start justify-between mb-8">
                <h2 className="text-gray-900">Active Pipeline</h2>
                <div className="w-52">
                  <p className="text-xs text-gray-500 mb-2">By expected close</p>
                  <Select
                    value={selectedFilter.value}
                    onValueChange={(value) => {
                      const option = dateFilterOptions.find(opt => opt.value === value);
                      if (option) setSelectedFilter(option);
                    }}
                  >
                    <SelectTrigger
                      className="w-full h-9 border-0 border-b border-gray-200 rounded-none px-0 hover:border-gray-400 focus-visible:border-[#4A90A4] focus-visible:ring-0 bg-white"
                    >
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-white">
                      {dateFilterOptions.map((option) => (
                        <SelectItem key={option.value} value={option.value}>
                          {option.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>

              {/* Funnel Visualization */}
              <div className="space-y-6">
                {/* Main Funnel Stages */}
                <div className="flex items-end gap-2">
                  {stages.map((stage, index) => (
                    <div key={stage.name} className="relative flex-1">
                      <FunnelStage
                        {...stage}
                        stageIndex={index}
                        totalStages={stages.length}
                        onStageClick={() => handleStageClick(stage.name)}
                        onAtRiskClick={() => handleAtRiskClick(stage.name)}
                      />
                      {index < stages.length - 1 && (
                        <div className="absolute -top-8 -right-1 transform translate-x-1/2 z-10 flex flex-col items-center">
                          {/* Conversion Badge with Arrow */}
                          <div className="flex items-center gap-0.5">
                            <div className="bg-white border border-[#4A90A4] rounded-full px-2.5 py-1 shadow-sm whitespace-nowrap">
                              <span className="text-xs text-[#4A90A4]">{stages[index].conversionRate}%</span>
                            </div>
                            <svg width="16" height="16" viewBox="0 0 16 16" fill="none" className="text-[#4A90A4]">
                              <path d="M6 4L10 8L6 12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </div>
                        </div>
                      )}
                    </div>
                  ))}
                </div>

                {/* Terminal Stages - Below and Right */}
                <div className="flex justify-end">
                  <div className="w-80">
                    <TerminalStages
                      won={terminalStages.won}
                      lost={terminalStages.lost}
                      onWonClick={() => handleStageClick('Closed Won')}
                      onLostClick={() => handleStageClick('Closed Lost')}
                    />
                  </div>
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="flex gap-3 pt-4">
              <Button
                onClick={handleViewAtRisk}
                className="bg-rose-600 hover:bg-rose-700 text-white px-6"
              >
                View At-Risk ({summary.atRisk})
              </Button>
              <Button
                onClick={handleViewAll}
                variant="outline"
                className="px-6 border-gray-300"
              >
                View All Opportunities
              </Button>
            </div>
          </div>
        )}
    </div>
  );
}
